import classNames from "classnames";
import map from "lodash/map";
import { SkillsFrameworksSidePanel } from "PFApp/components/activity_form/form_selectors/skills_frameworks_selector";
import { FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplate } from "PFApp/hooks";
import { useSelectedSkillsFrameworks } from "PFApp/hooks/activity_form/use_selected_skills_frameworks";
import { SidePanelType, useSidePanelActions } from "PFApp/stores/side_panels_store";
import { RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { Button } from "PFComponents/button";
import { Flex, Inline } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import { Typography } from "PFComponents/typography";
import { Id, SkillsFramework, Subtemplate } from "PFCore/types";
import { RefObject, useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useFormActivityCreator } from "../../../../hooks/use_form_activity_creator";
import css from "../../../../tabular_roles_form.module.scss";
import { usePopulateOnFrameworkIdsChange } from "./use_populate_on_framework_ids_change";

type SkillsFrameworksCellProps = {
  subtemplate: Subtemplate | null;
  roleFormKey: RoleFormKey;
  value: Id[];
  onChange: (newValue: Id[]) => void;
  disabled?: boolean;
  error?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const SkillsFrameworksCell = ({
  subtemplate,
  roleFormKey,
  value,
  onChange,
  disabled,
  error,
  inputFocusProps,
  buttonRef
}: SkillsFrameworksCellProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "form.sections.skillsFrameworks" });
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const roleTemplate = useTemplate(TemplateKey.Role)!;
  const { close: closeCustomValuePreview } = useSidePanelActions(SidePanelType.CustomValuePreview);
  const { selectedSkillsFrameworks, isSkillsFrameworksLoading } = useSelectedSkillsFrameworks(value);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [showUnlinkModal, setShowUnlinkModal] = useState(false);

  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });
  const activityCreator = useFormActivityCreator(roleFormKey);

  const { setAppliedFrameworkId } = usePopulateOnFrameworkIdsChange({
    value,
    roleFormKey,
    subtemplate
  });

  const areFrameworksAdded = value.length > 0;
  const isActionDisabled = disabled || (areFrameworksAdded && isSkillsFrameworksLoading);

  const handleAddClick = () => {
    closeCustomValuePreview();
    setShowSidePanel(true);
  };

  const handleRemoveClick = () => {
    setShowUnlinkModal(true);
  };

  const handleFrameworksChange = (frameworks: SkillsFramework[]) => {
    const newFrameworkIds = map(frameworks, "id");

    setAppliedFrameworkId(newFrameworkIds);
    onChange(newFrameworkIds);
  };

  return (
    <div className={classNames(css.cellFormSection, { [css.error]: error })} style={inputFocusProps?.style}>
      {isSkillsFrameworksLoading && <LoadingDots />}
      {!isSkillsFrameworksLoading &&
        (selectedSkillsFrameworks.length > 0 ? (
          <Inline wrap="wrap" gap="spacingSm">
            {selectedSkillsFrameworks.map(({ id, name }, index) => (
              <Inline key={id} className={css.skillFrameworkItem}>
                <Typography variant="labelBold" title={name} clipOverflow>
                  {name}
                </Typography>
                {index < selectedSkillsFrameworks.length - 1 && <Divider type="vertical" />}
              </Inline>
            ))}
          </Inline>
        ) : (
          <Typography variant="bodyRegular">{t("noSkillsFrameworksAdded")}</Typography>
        ))}
      <Flex className={css.stickyEditButtonContainer}>
        <Button
          ref={buttonRef}
          kind="text"
          className={css.editButton}
          onClick={areFrameworksAdded ? handleRemoveClick : handleAddClick}
          text={areFrameworksAdded ? t("remove") : t("add")}
          aria-label={
            areFrameworksAdded
              ? tBlueprints("removeSkillsFrameworksFromRole", {
                  roleName: roleName || tBlueprints("emptyRoleName")
                })
              : tBlueprints("addSkillsFrameworksInRole", {
                  roleName: roleName || tBlueprints("emptyRoleName")
                })
          }
          disabled={isActionDisabled}
          tabIndex={inputFocusProps?.tabIndex}
        />
      </Flex>

      {showUnlinkModal && (
        <Modal
          title={t("removeSkillsFrameworks")}
          onOK={() => {
            onChange([]);
          }}
          kindOK="danger"
          onClose={() => setShowUnlinkModal(false)}
        >
          {t("removeConfirmationMessage")}
        </Modal>
      )}
      {showSidePanel && (
        <SkillsFrameworksSidePanel
          selectedSkillsFrameworks={selectedSkillsFrameworks}
          template={roleTemplate}
          subtemplate={subtemplate}
          activityCreator={activityCreator}
          roleFormKeyPrefix={roleFormKey}
          closeOnClickOutside
          onClose={() => setShowSidePanel(false)}
          onFrameworksChange={handleFrameworksChange}
        />
      )}
    </div>
  );
};
